import React, { useState } from 'react';
import { formatPrice } from '../Prices/priceLookupShared';

// Order Discount on a Quote or Sales Order (issue #449, vis_sales_orders.md
// "Order Discount"). useOrderDiscount holds the form's discount; OrderDiscountRow
// is its row in the totals, in the same right-hand column as Shipping.

const NONE = { type: '', value: '', reason: '' };

const fromDocument = (doc) => (doc?.discount_type
  ? { type: doc.discount_type, value: String(Number(doc.discount_value)), reason: doc.discount_reason || '' }
  : NONE);

export function useOrderDiscount() {
  const [form, setForm] = useState(NONE);
  // What the document held when loaded (or the Quote being copied held): a
  // dollar discount left as it was is capped to the lines, not refused.
  const [saved, setSaved] = useState(NONE);
  const load = (doc) => {
    const next = fromDocument(doc);
    setForm(next);
    setSaved(next);
  };
  const set = (changes) => setForm((f) => ({ ...f, ...changes }));
  const remove = () => setForm(NONE);
  const add = () => setForm({ type: 'percent', value: '', reason: '' });
  const value = Number(form.value);
  return {
    form,
    load,
    set,
    add,
    remove,
    // For documentTotals; a discount still being typed takes nothing off.
    forTotals: form.type && value > 0 ? { type: form.type, value } : null,
    payload: {
      discount_type: form.type || null,
      discount_value: form.type ? value : null,
      discount_reason: form.type ? form.reason.trim() : null,
    },
    // Why Save can't go ahead, or null. The server checks the same.
    problem: (linesAmount) => {
      if (!form.type) return null;
      if (!(value > 0)) return 'Enter the discount, or remove it';
      if (!form.reason.trim()) return 'A discount needs a reason';
      if (form.type === 'percent' && value > 100) return 'A percentage discount can’t be more than 100%';
      const entered = form.type !== saved.type || value !== Number(saved.value);
      if (form.type === 'amount' && entered && value > linesAmount + 0.005) return 'A discount can’t be more than the value of the line items';
      return null;
    },
  };
}

// "Discount (10%)", or "Discount" for a dollar amount.
export function discountLabel(type, value) {
  return type === 'percent' ? `Discount (${Number(value)}%)` : 'Discount';
}

const ROW = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, padding: '4px 0', gap: 8 };
const NOTE = { fontSize: 11, color: '#5a6b85', textAlign: 'right', marginTop: -2, marginBottom: 4 };
const ADD = { background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, padding: 0 };

// discount: useOrderDiscount's. amount: what it takes off, in the view shown
// (the Ex GST / Inc GST switch). basisSuffix: the view's, for the label.
// entryBasis: the Customer's, which a dollar amount is always typed in.
// locked: past the point it can change. gutter: the width of the calculator
// button beside Shipping's figure, so this figure lines up with it.
export default function OrderDiscountRow({ discount, amount, basisSuffix, entryBasis, locked, gutter }) {
  const { form } = discount;
  if (!form.type) {
    if (locked) return null;
    return (
      <div style={{ ...ROW, paddingRight: gutter }}>
        <button type="button" style={ADD} title="Add a discount across this document's line items" onClick={discount.add}>Add Discount</button>
      </div>
    );
  }
  return (
    <>
      <div style={{ ...ROW, paddingRight: gutter }}>
        <span>{discountLabel(form.type, form.value)}{basisSuffix}</span>
        <span>{`-${formatPrice(amount)}`}</span>
      </div>
      {locked ? (
        <div style={{ ...NOTE, paddingRight: gutter }}>{form.reason}</div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4, marginBottom: 4, paddingRight: gutter, flexWrap: 'wrap' }}>
          <select
            className="styled-input"
            aria-label="Discount type"
            value={form.type}
            onChange={(e) => discount.set({ type: e.target.value })}
            style={{ width: 54, fontSize: 11, padding: '2px 4px', height: 22 }}
          >
            <option value="percent">%</option>
            <option value="amount">$</option>
          </select>
          <input
            className="styled-input"
            type="number"
            min="0"
            step="0.01"
            aria-label="Discount"
            title={form.type === 'amount' && entryBasis ? `Enter the amount ${entryBasis}` : undefined}
            placeholder={form.type === 'amount' && entryBasis ? entryBasis : 'Discount'}
            value={form.value}
            onChange={(e) => discount.set({ value: e.target.value })}
            style={{ width: 76, fontSize: 11, padding: '2px 6px', height: 22, textAlign: 'right' }}
          />
          <input
            className="styled-input"
            type="text"
            maxLength={255}
            aria-label="Discount reason"
            placeholder="Reason (required)"
            value={form.reason}
            onChange={(e) => discount.set({ reason: e.target.value })}
            style={{ width: 140, fontSize: 11, padding: '2px 6px', height: 22 }}
          />
          <button type="button" style={{ ...ADD, fontSize: 11 }} title="Remove this discount" onClick={discount.remove}>Remove</button>
        </div>
      )}
    </>
  );
}
